import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import Swal from "sweetalert2";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import { BASE_URL } from "../Apis/apiConfig";
import { Building,CreditCard, MapPin, Hash, QrCode, User, RefreshCw, AlertCircle, FileX, X, Trash2, Edit2,
  Eye, Download, Upload, Plus, Search, CheckSquare, 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BankDetails = () => {
  const [bank, setBankData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [newBank, setNewBank] = useState({
    accountHolderName: "",
    accountNumber: "",
    branchName: "",
    backAddress: "",
    ifsccode: "",
    qrcodeUpload: "",
  });

  const navigate = useNavigate();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const fetchBankDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const response = await axios.post(
        `${BASE_URL}/back/get-back-details`,
        null,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setBankData(response.data || []);
    } catch (error) {
      console.error(error);
      setError(error?.response?.data || "Failed to fetch bank details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const deleteBankDetails = async (backId) => {
    const result = await Swal.fire({
      title: "Delete Bank Details?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      await fetch(
        `${BASE_URL}/back/delete-back-details?backId=${backId}&societyName=${encodeURIComponent(
          societyName,
        )}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setBankData((prev) => prev.filter((b) => b.backId !== backId));
      setSelectedBanks((prev) => prev.filter((id) => id !== backId));

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Bank detail deleted successfully",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  const deleteSelectedBanks = async () => {
    if (selectedBanks.length === 0) {
      Swal.fire("Info", "Please select bank details to delete", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `This will delete ${selectedBanks.length} selected bank detail(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const deletePromises = selectedBanks.map((backId) =>
        fetch(
          `${BASE_URL}/back/delete-back-details?backId=${backId}&societyName=${encodeURIComponent(
            societyName,
          )}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ),
      );

      await Promise.all(deletePromises);

      setBankData((prev) =>
        prev.filter((b) => !selectedBanks.includes(b.backId)),
      );
      setSelectedBanks([]);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: `${selectedBanks.length} bank detail(s) deleted successfully`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Error", "Delete failed", "error");
    }
  };
const updateBankDetails = async () => {
  try {
    const token = localStorage.getItem("token");

    const formData = new FormData();

    formData.append("backId", selectedBank.backId);
    formData.append("accountHolderName", selectedBank.accountHolderName);
    formData.append("bankName", selectedBank.bankName || "");
    formData.append("accountNumber", selectedBank.accountNumber);
    formData.append("IFSCCode", selectedBank.ifsccode);
    formData.append("branchName", selectedBank.branchName);
    formData.append("bankAddress", selectedBank.backAddress);
    formData.append("societyName", selectedBank.societyName);

    // Only append QR if updated
    if (selectedBank.qrcodeUploadFile) {
      formData.append("QRCodeUpload", selectedBank.qrcodeUploadFile);
    }

    await axios.post(
      `${BASE_URL}/back/edit-bank-details`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    Swal.fire({
      icon: "success",
      title: "Updated!",
      text: "Bank details updated successfully",
      timer: 1500,
      showConfirmButton: false,
    });

    setIsEditOpen(false);
    fetchBankDetails();

  } catch (error) {
    console.error(error);
    Swal.fire("Error", "Update failed", "error");
  }
};

  const addNewBank = async () => {
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const payload = {
        ...newBank,
        societyName,
      };

      const response = await axios.post(
        `${BASE_URL}/add-back-details`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setBankData((prev) => [...prev, response.data]);
      setIsAddOpen(false);
      setNewBank({
        accountHolderName: "",
        accountNumber: "",
        branchName: "",
        backAddress: "",
        ifsccode: "",
        qrcodeUpload: "",
      });

      Swal.fire({
        icon: "success",
        title: "Added!",
        text: "Bank details added successfully",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Error", "Add failed", "error");
    }
  };

  const handleQrUpload = (e, isNew = false) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      if (isNew) {
        setNewBank((prev) => ({ ...prev, qrcodeUpload: base64 }));
      } else {
        setSelectedBank({ ...selectedBank, qrcodeUpload: base64 });
      }
    };
    reader.readAsDataURL(file);
  };

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return bank;
    const searchLower = searchQuery.toLowerCase();
    return bank.filter((item) => {
      return (
        (item.accountHolderName || "").toLowerCase().includes(searchLower) ||
        (item.accountNumber || "").toLowerCase().includes(searchLower) ||
        (item.branchName || "").toLowerCase().includes(searchLower) ||
        (item.ifsccode || "").toLowerCase().includes(searchLower)
      );
    });
  }, [bank, searchQuery]);

  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [itemsPerPage, totalItems, currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${
              totalPages === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };

  const selectAllRows = (e) => {
    if (e.target.checked) {
      const allIds = paginatedData
        .map((item) => item.backId)
        .filter((id) => id !== undefined);
      setSelectedBanks(allIds);
    } else {
      setSelectedBanks([]);
    }
  };

  const handleRowSelect = (id) => {
    if (!id) return;

    setSelectedBanks((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="app-container">
      <style>
        {`
          /* ========== BANK DETAILS PAGE OPTIMIZATIONS ========== */
          .app-container {
            height: 100vh;
            overflow: hidden !important;
          }

          .main-layout {
            height: calc(100vh - 64px);
            overflow: hidden !important;
          }

          .content-area {
            height: 100%;
            overflow-y: auto !important;
            overflow-x: hidden;
            padding-bottom: 20px;
          }

          .financial-container,
          .financial-card {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .financial-body {
            height: auto !important;
            overflow: visible !important;
            padding: 0 !important;
          }

          /* ========== COMPACT TABLE CONTROLS ========== */
          .controls-left,
          .controls-center,
          .controls-right {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .controls-center {
            flex: 1;
            justify-content: center;
          }

          .entries-selector label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: white;
            font-weight: 500;
          }

          .entries-select {
            padding: 4px 8px;
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.1);
            font-size: 12px;
            color: white;
            cursor: pointer;
            height: 28px;
          }

          .entries-select option {
            background: #1e40af;
            color: white;
          }

          .pagination-controls {
            display: flex;
            align-items: center;
            gap: 4px;
            border: none;
          }

          .pagination-nav {
            padding: 4px 10px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            cursor: pointer;
            color: white;
            font-size: 11px;
            font-weight: 500;
            height: 26px;
            transition: all 0.2s;
          }

          .pagination-nav:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .pagination-nav:not(:disabled):hover {
            background: rgba(255, 255, 255, 0.2);
          }

          .pagination-btn {
            min-width: 26px;
            height: 26px;
            padding: 4px 8px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            cursor: pointer;
            color: white;
            font-size: 11px;
            font-weight: 500;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .pagination-btn:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          .pagination-btn.active {
            background: white;
            color: #0d6efd;
            border-color: white;
          }

          .pagination-ellipsis {
            padding: 0 4px;
            color: rgba(255, 255, 255, 0.7);
            font-size: 11px;
          }

          .entries-info {
            font-size: 11px;
            color: white;
            white-space: nowrap;
          }

          /* ========== TABLE OPTIMIZATION ========== */
          .financial-table-container {
            overflow-x: auto;
            padding: 0;
            margin: 0;
          }

          .financial-table,
          .data-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
          }

          .financial-table th,
          .data-table th {
            padding: 6px 8px !important;
            text-align: center !important;
            vertical-align: middle;
            white-space: nowrap;
            font-weight: 600;
            font-size: 11px !important;
            background-color: #f8fafc;
            border-bottom: 2px solid #e2e8f0;
            border-right: 1px solid #e2e8f0;
            line-height: 1.2 !important;
            height: 32px !important;
          }

          .financial-table td,
          .data-table td {
            padding: 5px 8px !important;
            text-align: center !important;
            vertical-align: middle;
            font-size: 10px !important;
            border-bottom: 1px solid #e2e8f0;
            border-right: 1px solid #e2e8f0;
            line-height: 1.3 !important;
            height: 36px !important;
          }

          /* Checkbox Column */
          .financial-table th:nth-child(1),
          .financial-table td:nth-child(1),
          .data-table th:first-child,
          .data-table td:first-child {
            width: 35px !important;
            min-width: 35px !important;
            max-width: 35px !important;
            padding: 5px 2px !important;
          }

          /* Actions Column */
          .financial-table th:last-child,
          .financial-table td:last-child,
          .data-table th:last-child,
          .data-table td:last-child {
            width: 100px !important;
            min-width: 100px !important;
            padding: 4px 2px !important;
          }

          /* Action Buttons */
          .action-buttons {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 4px;
            padding: 0;
            margin: 0;
          }

          .action-icon-btn,
          .icon-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 3px;
            border-radius: 3px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.2s;
            width: 24px;
            height: 24px;
          }

          .action-icon-btn svg,
          .icon-btn svg {
            width: 14px;
            height: 14px;
          }

          .action-icon-btn:hover,
          .icon-btn:hover {
            background-color: #f1f5f9;
          }

          .icon-btn.edit {
            color: #0d6efd;
          }

          /* Checkbox */
          .financial-table input[type="checkbox"],
          .data-table input[type="checkbox"] {
            margin: 0;
            width: 13px;
            height: 13px;
            vertical-align: middle;
            cursor: pointer;
          }

          /* Row Hover */
          .financial-table tbody tr:hover td,
          .data-table tbody tr:hover td {
            background-color: #f8fafc;
          }

          .selected-row td {
            background-color: #e3f2fd !important;
          }

          /* Cell Content */
          .cell-content {
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
          }

          /* Empty State */
          .financial-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            min-height: 300px;
          }

          .financial-state h3 {
            margin: 16px 0 8px 0;
            color: #334155;
            font-size: 18px;
            font-weight: 600;
          }

          .financial-state p {
            color: #64748b;
            font-size: 14px;
            margin: 0;
          }

          /* Loading Animation */
          .spin-animation {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          /* Responsive */
          @media (max-width: 768px) {
            .controls-left,
            .controls-center,
            .controls-right {
              width: 100%;
              justify-content: space-between;
            }
          }
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              <div className="flex px-4 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-tr-lg rounded-tl-lg text-white py-2">
                <div className="controls-left">
                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={itemsPerPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>

                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search bank details..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchQuery && (
                        <button
                          onClick={handleClearSearch}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={fetchBankDetails}
                      disabled={loading}
                      title="Refresh bank details"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none gap-2 flex items-center text-left bg-red-300 text-white hover:bg-red-500 px-2 py-1"
                      onClick={deleteSelectedBanks}
                      disabled={selectedBanks.length === 0}
                      title={`Delete ${selectedBanks.length} selected bank detail(s)`}
                    >
                      <Trash2 size={14} />
                      <span style={{ fontSize: "11px" }}>
                        {selectedBanks.length}
                      </span>
                      <CheckSquare size={14} />
                    </button>

                    <button
                      type="button"
                      className="rounded border-none flex align-center bg-green-500 text-white hover:bg-green-700 p-2"
                      onClick={() => navigate("/add-bank-details")}
                      title="Add new bank details"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                <div className="controls-right">
                  {!loading && totalItems > 0 && (
                    <span className="entries-info text-white">
                      {(currentPage - 1) * itemsPerPage + 1}-
                      {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                      {totalItems}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>

                    {renderPageNumbers()}

                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Bank Details</h3>
                    <p>Please wait while we fetch the bank information...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{error}</p>
                    <button
                      type="button"
                      className="action-btn primary-btn"
                      onClick={fetchBankDetails}
                      style={{ marginTop: "16px" }}
                    >
                      <RefreshCw size={16} />
                      Try Again
                    </button>
                  </div>
                ) : bank.length === 0 ? (
                  <div className="financial-state">
                    <FileX size={48} />
                    <h3>No Bank Details Found</h3>
                    <p>There are no bank details available for this society.</p>
                    {/* <button
                      type="button"
                     
                      onClick={() => navigate("/add-bank-details")}
                      
                    >
                      Add First Bank Detail
                    </button> */}
                    <button
  type="button"
  onClick={() => navigate("/add-bank-details")}
  style={{
    marginTop: "16px",
    padding: "10px 18px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
    transition: "all 0.2s ease",
  }}
  onMouseOver={(e) => (e.target.style.opacity = "0.9")}
  onMouseOut={(e) => (e.target.style.opacity = "1")}
>
  + Add First Bank Detail
</button>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={selectAllRows}
                              checked={
                                selectedBanks.length > 0 &&
                                selectedBanks.length ===
                                  paginatedData.filter((item) => item.backId)
                                    .length
                              }
                            />
                          </th>
                          <th style={{ width: "60px" }}>Sr No</th>
                          <th>Account Holder</th>
                          <th>Account Number</th>
                          <th>Branch</th>
                          <th>Address</th>
                          <th>IFSC Code</th>
                          <th style={{ width: "80px" }}>QR Code</th>
                          <th style={{ width: "100px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item, index) => {
                          const itemId = item.backId;
                          return (
                            <tr
                              key={itemId}
                              className={
                                selectedBanks.includes(itemId)
                                  ? "selected-row"
                                  : ""
                              }
                            >
                              <td>
                                {itemId && (
                                  <input
                                    type="checkbox"
                                    checked={selectedBanks.includes(itemId)}
                                    onChange={() => handleRowSelect(itemId)}
                                  />
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td>
                                <div className="cell-content">
                                  <div className="flex items-center gap-2 justify-center">
                                    <User size={12} className="text-gray-500" />
                                    <span>{item.accountHolderName || "-"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="cell-content">
                                  <div className="flex items-center gap-2 justify-center">
                                    <Hash size={12} className="text-gray-500" />
                                    <span>{item.accountNumber || "-"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="cell-content">
                                  <div className="flex items-center gap-2 justify-center">
                                    <Building
                                      size={12}
                                      className="text-gray-500"
                                    />
                                    <span>{item.branchName || "-"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="cell-content">
                                  <div className="flex items-center gap-2 justify-center">
                                    <MapPin
                                      size={12}
                                      className="text-gray-500"
                                    />
                                    <span>{item.backAddress || "-"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="cell-content">
                                  <div className="flex items-center gap-2 justify-center">
                                    <CreditCard
                                      size={12}
                                      className="text-gray-500"
                                    />
                                    <span className="font-mono">
                                      {item.ifsccode || "-"}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {item.qrcodeUpload && (
                                  <button
                                    type="button"
                                    className="action-icon-btn"
                                    onClick={() =>
                                      setZoomedImage(
                                        `data:image/png;base64,${item.qrcodeUpload}`,
                                      )
                                    }
                                    title="View QR Code"
                                  >
                                    <QrCode
                                      size={14}
                                      className="text-blue-600"
                                    />
                                  </button>
                                )}
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    type="button"
                                    className="icon-btn edit"
                                    onClick={() => {
                                      setSelectedBank(item);
                                      setIsEditOpen(true);
                                    }}
                                    title="Edit"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="icon-btn edit"
                                    onClick={() =>
                                      deleteBankDetails(item.backId)
                                    }
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="floating-btn"
            onClick={() => setIsAddOpen(true)}
            aria-label="Add new bank details"
          >
            <Plus size={24} />
          </button>
          {/* {isEditOpen && selectedBank && (
            <div
              className="modal-overlay edit-modal-overlay "
              onClick={() => setIsEditOpen(false)}
            >
              <div
                className="border bg-blue-100 "
                onClick={(e) => e.stopPropagation()}
              >
                <div className="edit-modal-header ">
                  <h3 className="edit-modal-title">
                    <Edit2 size={20} />
                    Edit Bank Details
                  </h3>
                  <button
                    type="button"
                    className="edit-modal-close"
                    onClick={() => setIsEditOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="edit-modal-body">
                  <div className="edit-form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <User size={14} />
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedBank.accountHolderName || ""}
                        onChange={(e) =>
                          setSelectedBank({
                            ...selectedBank,
                            accountHolderName: e.target.value,
                          })
                        }
                        placeholder="Enter account holder name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <CreditCard size={14} />
                        Account Number
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedBank.accountNumber || ""}
                        onChange={(e) =>
                          setSelectedBank({
                            ...selectedBank,
                            accountNumber: e.target.value,
                          })
                        }
                        placeholder="Enter account number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <Building size={14} />
                        Branch Name
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedBank.branchName || ""}
                        onChange={(e) =>
                          setSelectedBank({
                            ...selectedBank,
                            branchName: e.target.value,
                          })
                        }
                        placeholder="Enter branch name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <Hash size={14} />
                        IFSC Code
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedBank.ifsccode || ""}
                        onChange={(e) =>
                          setSelectedBank({
                            ...selectedBank,
                            ifsccode: e.target.value,
                          })
                        }
                        placeholder="Enter IFSC code"
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ gridColumn: "span 2" }}
                    >
                      <label className="form-label">
                        <MapPin size={14} />
                        Bank Address
                      </label>
                      <textarea
                        className="form-input"
                        rows={3}
                        value={selectedBank.backAddress || ""}
                        onChange={(e) =>
                          setSelectedBank({
                            ...selectedBank,
                            backAddress: e.target.value,
                          })
                        }
                        placeholder="Enter bank address"
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ gridColumn: "span 2" }}
                    >
                      <label className="form-label">
                        <QrCode size={14} />
                        QR Code Image
                      </label>
                      <div className="qr-upload-section">
                        {selectedBank.qrcodeUpload && (
                          <div className="qr-preview">
                            <img
                              src={`data:image/png;base64,${selectedBank.qrcodeUpload}`}
                              alt="QR Preview"
                              width={120}
                              style={{
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            <button
                              type="button"
                              className="view-qr-btn"
                              onClick={() =>
                                setZoomedImage(
                                  `data:image/png;base64,${selectedBank.qrcodeUpload}`,
                                )
                              }
                            >
                              <Eye size={14} />
                              View
                            </button>
                          </div>
                        )}
                        <div className="upload-control">
                          <label className="upload-btn">
                            <Upload size={16} />
                            Upload QR Code
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleQrUpload(e, false)}
                              style={{ display: "none" }}
                            />
                          </label>
                          <p className="upload-hint">PNG, JPG up to 5MB</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions ">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => setIsEditOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-save"
                      onClick={updateBankDetails}
                    >
                      Update Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )} */}
          {isEditOpen && selectedBank && (
  <div
    className="modal-overlay edit-modal-overlay"
    onClick={() => setIsEditOpen(false)}
  >
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        width: "600px",
        maxWidth: "95%",
        padding: "20px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#2563eb" }}>
          Edit Bank Details
        </h3>

        <button onClick={() => setIsEditOpen(false)}>
          <X size={20} />
        </button>
      </div>

      {/* FORM */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "15px",
        }}
      >
        {/* Account Holder */}
        <div>
          <label className="form-label">Account Holder</label>
          <input
            className="form-input"
            value={selectedBank.accountHolderName || ""}
            onChange={(e) =>
              setSelectedBank({
                ...selectedBank,
                accountHolderName: e.target.value,
              })
            }
          />
        </div>

        {/* Account Number */}
        <div>
          <label className="form-label">Account Number</label>
          <input
            className="form-input"
            value={selectedBank.accountNumber || ""}
            onChange={(e) =>
              setSelectedBank({
                ...selectedBank,
                accountNumber: e.target.value,
              })
            }
          />
        </div>

        {/* Branch */}
        <div>
          <label className="form-label">Branch</label>
          <input
            className="form-input"
            value={selectedBank.branchName || ""}
            onChange={(e) =>
              setSelectedBank({
                ...selectedBank,
                branchName: e.target.value,
              })
            }
          />
        </div>

        {/* IFSC */}
        <div>
          <label className="form-label">IFSC Code</label>
          <input
            className="form-input"
            value={selectedBank.ifsccode || ""}
            onChange={(e) =>
              setSelectedBank({
                ...selectedBank,
                ifsccode: e.target.value,
              })
            }
          />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label className="form-label">Bank Address</label>
          <textarea
            rows={3}
            className="form-input"
            value={selectedBank.backAddress || ""}
            onChange={(e) =>
              setSelectedBank({
                ...selectedBank,
                backAddress: e.target.value,
              })
            }
          />
        </div>
        <div style={{ gridColumn: "span 2", textAlign: "center" }}>
          <label className="form-label">QR Code</label>

          {selectedBank.qrcodeUpload && (
            <img
              src={`data:image/png;base64,${selectedBank.qrcodeUpload}`}
              alt="QR"
              style={{
                width: "120px",
                margin: "10px auto",
                borderRadius: "8px",
                border: "1px solid #ddd",
              }}
            />
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleQrUpload(e, false)}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "20px",
        }}
      >
        <button className="btn-cancel" onClick={() => setIsEditOpen(false)}>
          Cancel
        </button>

        <button className="btn-save" onClick={updateBankDetails}>
          Update Details
        </button>
      </div>
    </div>
  </div>
)}
          {isAddOpen && (
            <div
              className="modal-overlay edit-modal-overlay"
              onClick={() => setIsAddOpen(false)}
            >
              <div
                className="modal-content edit-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="edit-modal-header">
                  <h3 className="edit-modal-title">
                    <Plus size={20} />
                    Add New Bank Details
                  </h3>
                  <button
                    type="button"
                    className="edit-modal-close"
                    onClick={() => setIsAddOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="edit-modal-body">
                  <div className="edit-form-grid">
                    <div className="form-group">
                      <label className="form-label">Account Holder Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newBank.accountHolderName}
                        onChange={(e) =>
                          setNewBank({
                            ...newBank,
                            accountHolderName: e.target.value,
                          })
                        }
                        placeholder="Enter account holder name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Account Number</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newBank.accountNumber}
                        onChange={(e) =>
                          setNewBank({
                            ...newBank,
                            accountNumber: e.target.value,
                          })
                        }
                        placeholder="Enter account number"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Branch Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newBank.branchName}
                        onChange={(e) =>
                          setNewBank({ ...newBank, branchName: e.target.value })
                        }
                        placeholder="Enter branch name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">IFSC Code</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newBank.ifsccode}
                        onChange={(e) =>
                          setNewBank({ ...newBank, ifsccode: e.target.value })
                        }
                        placeholder="Enter IFSC code"
                      />
                    </div>
                    <div
                      className="form-group"
                      style={{ gridColumn: "span 2" }}
                    >
                      <label className="form-label">Bank Address</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        value={newBank.backAddress}
                        onChange={(e) =>
                          setNewBank({
                            ...newBank,
                            backAddress: e.target.value,
                          })
                        }
                        placeholder="Enter bank address"
                      />
                    </div>
                    <div
                      className="form-group"
                      style={{ gridColumn: "span 2" }}
                    >
                      <label className="form-label">QR Code Image</label>
                      <div className="upload-control">
                        <label className="upload-btn">
                          <Upload size={16} />
                          Upload QR Code
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleQrUpload(e, true)}
                            style={{ display: "none" }}
                          />
                        </label>
                        {newBank.qrcodeUpload && (
                          <div className="qr-preview-small">
                            <span className="text-green-600">
                              ✓ QR Code uploaded
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => setIsAddOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-save"
                      onClick={addNewBank}
                    >
                      Add Bank Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {zoomedImage && (
            <div
              className="modal-overlay image-zoom-overlay"
              onClick={() => setZoomedImage(null)}
            >
              <div
                className="image-zoom-container"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="image-zoom-header">
                  <h3>QR Code</h3>
                  <button
                    type="button"
                    className="image-zoom-close"
                    onClick={() => setZoomedImage(null)}
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="image-zoom-body">
                  <img
                    src={zoomedImage}
                    alt="Zoomed QR Code"
                    className="zoomed-image"
                  />
                </div>
                <div className="image-zoom-footer">
                  <button
                    type="button"
                    className="rounded border-none p-2 primary-btn"
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = zoomedImage;
                      link.download = "qrcode.png";
                      link.click();
                    }}
                  >
                    <Download size={16} />
                    Download QR Code
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BankDetails;
